





























































































































































































import Vue, { PropType, VueConstructor } from "vue";
import VaButton from "@/components/atoms/VaButton.vue";
import wizardStepsMixin, {
  WizardMixin,
} from "@/components/mixins/wizardStepsMixin";
import cloneDeep from "lodash/clone";
import { WizardConfiguration } from "@/models/wizardConfiguration";
import validationMixin from "@/components/mixins/validationMixin";
import {
  CalDavConfigurationForm,
  ExternalProvider,
  ExchangeConfigurationForm,
  createExchangeConfigurationForm,
  FeaturePayload,
  PodExternalAccountConfiguration,
  ProviderAccount,
} from "@/models/providerAccount";
import * as externalProvidersService from "@/services/externalProvidersService";
import SafariLoginDialog from "../safariLoginDialog.vue";
import ExchangeConfigurationDialog from "@/components/organisms/rooms/ExchangeConfigurationDialog.vue";
import LiveCollaborationConfigurationDialog from "@/components/organisms/rooms/LiveCollaborationConfigurationDialog.vue";
import NotificationService from "@/services/notificationService";

export default (Vue as VueConstructor<WizardMixin>).extend({
  name: "StepCalendar",
  mixins: [wizardStepsMixin, validationMixin],
  data() {
    return {
      externalAccounts: null as PodExternalAccountConfiguration | null,
      microsoftCalendarLogo: require("@/assets/images/providers/calendars/microsoft.png"),
      googleCalendarLogo: require("@/assets/images/providers/calendars/google.png"),
      liveCollaborationCalendarLogo: require("@/assets/images/providers/calendars/live_collaboration.png"),
      // Login URLs pre-caricati
      loginUrls: {} as Record<string, string>,
      // Login dialog state for Safari iOS popup blocking workaround
      pendingLoginUrl: null as string | null,
      pendingProvider: null as ExternalProvider | null,
      exchangeDialogOpen: false,
      exchangeConfig: createExchangeConfigurationForm(),
      exchangeSubmitCancelled: false,
      liveCollaborationDialogOpen: false,
    };
  },
  props: {
    activeStep: {
      type: Number,
      required: false,
      default: 0,
    },
    allowedProviders: {
      type: Array as PropType<ExternalProvider[]>,
      default: () => [],
    },
  },
  components: {
    VaButton,
    SafariLoginDialog,
    ExchangeConfigurationDialog,
    LiveCollaborationConfigurationDialog,
  },
  computed: {
    isVisible(): boolean {
      return this.activeStep === this.index + 1;
    },
    /**
     * Connect makes the backend ask the room to try the account. While the room is being
     * activated it asks on the wizard hub; a room build that does not answer there makes the
     * request time out (about a minute) and the account is then connected from the room page.
     */
    isLiveCollaborationAllowed(): boolean {
      return this.allowedProviders.includes("LiveCollaboration");
    },
    isLiveCollaborationCalendarEnabled(): boolean {
      return (
        this.getAccount("LiveCollaboration")?.features.some(
          (feature) => feature.featureType === "Calendar"
        ) ?? false
      );
    },
    liveCollaborationUsername(): string {
      const account = this.getAccount("LiveCollaboration");
      return (
        externalProvidersService.liveCollaborationAccountName(account) ?? ""
      );
    },
  },
  methods: {
    isProviderAllowed(provider: ExternalProvider): boolean {
      if (!this.allowedProviders || this.allowedProviders.length === 0)
        return true;
      return this.allowedProviders.includes(provider);
    },
    getAccount(provider: ExternalProvider): ProviderAccount | undefined {
      return this.externalAccounts?.accounts?.find(
        (account: ProviderAccount) => account.provider === provider
      );
    },
    getConnectedEmail(provider: ExternalProvider): string {
      return this.getAccount(provider)?.email || "";
    },
    isEnabled(provider: ExternalProvider): boolean {
      return !!this.getAccount(provider)?.email;
    },
    async enableCalendar(provider: ExternalProvider) {
      const clientRedirectUrl = `${window.location.origin}/pub/closeme`;
      const featurePayload = {
        provider,
        feature: "Calendar",
        enabled: true,
        config: null,
      } as FeaturePayload;

      await externalProvidersService.wizardToggleFeature(
        featurePayload,
        this.deviceConfiguration.deviceId,
        this.deviceConfiguration.deviceConfigurationId,
        this.deviceConfiguration.sessionId,
        clientRedirectUrl
      );
    },
    onExchangeActionClick() {
      this.exchangeSubmitCancelled = false;
      this.exchangeDialogOpen = true;
    },
    onExchangeDialogCancel() {
      this.exchangeSubmitCancelled = true;
      this.loading = false;
    },
    async onExchangeDialogSubmit(config: ExchangeConfigurationForm) {
      this.loading = true;
      this.exchangeSubmitCancelled = false;
      try {
        this.exchangeConfig = {
          ...config,
        };
        const featurePayload = {
          provider: "MicrosoftExchange",
          feature: "Calendar",
          enabled: true,
          config: this.exchangeConfig,
        } as unknown as FeaturePayload;

        await externalProvidersService.wizardToggleFeature(
          featurePayload,
          this.deviceConfiguration.deviceId,
          this.deviceConfiguration.deviceConfigurationId,
          this.deviceConfiguration.sessionId
        );
        if (this.exchangeSubmitCancelled) return;
        await this.refreshExternalAccounts();
        if (this.exchangeSubmitCancelled) return;
        this.onFinish();
      } catch (e) {
        if (!this.exchangeSubmitCancelled) {
          this.handleError(e as Error);
        }
      } finally {
        this.loading = false;
      }
    },

    async toggleLiveCollaborationFeature(
      feature: "Default" | "Calendar",
      enabled: boolean,
      config: Record<string, string> | null = null
    ) {
      const featurePayload = {
        provider: "LiveCollaboration",
        feature,
        enabled,
        config,
      } as unknown as FeaturePayload;
      await externalProvidersService.wizardToggleFeature(
        featurePayload,
        this.deviceConfiguration.deviceId,
        this.deviceConfiguration.deviceConfigurationId,
        this.deviceConfiguration.sessionId
      );
    },
    /**
     * No OAuth: the account typed in the dialog is connected (kept on the Default feature, as
     * an OAuth connection would be) and the calendar is switched on. Turning it off works as on
     * the room page: the calendar goes off first (it needs the account), then the external
     * account is deleted like the other providers'.
     */
    async setLiveCollaborationCalendar(
      enabled: boolean,
      config: Record<string, string> | null
    ) {
      try {
        if (enabled) {
          await this.toggleLiveCollaborationFeature("Default", true, config);
          await this.toggleLiveCollaborationFeature("Calendar", true);
        } else {
          const account = this.getAccount("LiveCollaboration");
          if (
            account?.features.some((f) => f.featureType === "Calendar") ?? false
          ) {
            await this.toggleLiveCollaborationFeature("Calendar", false);
          }
          if (account?.podExternalAccountId) {
            await externalProvidersService.wizardDisableProvider(
              account.podExternalAccountId,
              this.deviceConfiguration.deviceConfigurationId,
              this.deviceConfiguration.sessionId
            );
          }
        }
      } finally {
        await this.refreshExternalAccounts();
      }
    },
    /** A refusal is shown and rethrown, so the dialog stays open with what was typed. */
    async onLiveCollaborationDialogSubmit(config: CalDavConfigurationForm) {
      this.loading = true;
      try {
        await this.setLiveCollaborationCalendar(
          true,
          externalProvidersService.liveCollaborationAccountConfig(config)
        );
      } catch (e) {
        // EA402 is the room's own check failing: wrong account, or a network the calendar
        // server does not accept.
        const code = (e as { response?: { data?: { errorCode?: string } } })
          ?.response?.data?.errorCode;
        if (code === "EA402") {
          NotificationService.error(
            externalProvidersService.translateOr(
              "providers.liveCollaboration.connectRefused",
              "The room could not sign in to the Live Collaboration calendar with this username and password. Check them, and make sure the room is online."
            )
          );
        } else {
          NotificationService.error(e as never);
        }
        throw e;
      } finally {
        this.loading = false;
      }
      this.onFinish();
    },
    async onLiveCollaborationCalendarDisable() {
      this.loading = true;
      try {
        await this.setLiveCollaborationCalendar(false, null);
      } catch (e) {
        NotificationService.error(e as never);
      } finally {
        this.loading = false;
      }
    },

    async onCalendarSelect(provider: ExternalProvider) {
      const loginUrl = this.loginUrls[provider];
      this.loading = true;
      if (loginUrl) {
        if (externalProvidersService.isSafari()) {
          // Safari blocks popups after await, show dialog for user to click
          this.pendingLoginUrl = loginUrl;
          this.pendingProvider = provider;
        } else {
          if (provider === "Zoom") {
            externalProvidersService.openZoomLoginWithLogout(
              loginUrl,
              this.onPopupcalendarClosed
            );
          } else {
            externalProvidersService.openLoginPopup(
              loginUrl,
              this.onPopupcalendarClosed
            );
          }
          // externalProvidersService.openLoginPopup(
          //   loginUrl,
          //   this.onPopupcalendarClosed
          // );
        }
      } else {
        try {
          await this.enableCalendar(provider);
          await this.refreshExternalAccounts();
          this.onFinish();
        } catch (e) {
          const errorCode = (e as any)?.response?.data?.errorCode;
          const newLoginUrl = (e as any)?.response?.data?.errorData?.loginUrl;
          if (errorCode === "EA401" && newLoginUrl) {
            this.loginUrls[provider] = newLoginUrl;
            await this.onCalendarSelect(provider);
            return;
          }
          this.handleError(e as Error);
          this.loading = false;
        }
      }
    },

    async refreshExternalAccounts() {
      const deviceConfigurationId =
        this.deviceConfiguration?.deviceConfigurationId;
      const sessionId = this.deviceConfiguration?.sessionId;

      if (!deviceConfigurationId || !sessionId) {
        return;
      }

      try {
        this.externalAccounts =
          (await externalProvidersService.getWizardExternalAccounts(
            deviceConfigurationId,
            sessionId
          )) as PodExternalAccountConfiguration;
      } catch (e) {
        const errorCode = (e as any)?.response?.data?.errorCode;
        if (errorCode === "WEA-401" || errorCode === "WEA-402") {
          this.$notification.error(e);
          return;
        }
        this.handleError(e as Error);
      }
    },

    async onProviderDisconnect(provider: ExternalProvider) {
      this.loading = true;
      try {
        const account = this.getAccount(provider);
        if (!account) {
          return;
        }
        await externalProvidersService.wizardDisableProvider(
          account?.podExternalAccountId,
          this.deviceConfiguration.deviceConfigurationId,
          this.deviceConfiguration.sessionId
        );
        await this.refreshExternalAccounts();
      } catch (e) {
        this.handleError(e as Error);
      } finally {
        this.loading = false;
      }
    },

    async onPopupcalendarClosed() {
      try {
        await this.refreshExternalAccounts();
        this.onFinish();
      } catch (e) {
        const errorCode = (e as any)?.response?.data?.errorCode;
        if (errorCode === "WEA-401" || errorCode === "WEA-402") {
          this.$notification.error(e);
        }
      } finally {
        this.loading = false;
        this.pendingLoginUrl = null;
        this.pendingProvider = null;
      }
    },

    onFinish() {
      const res = this.wizardConfiguration
        ? cloneDeep(this.wizardConfiguration)
        : ({} as WizardConfiguration);
      res.externalAccounts = this.externalAccounts;
      this.$emit("onUpdateConfig", res);
      this.onNext();
    },
  },
  watch: {
    isVisible: {
      immediate: true,
      async handler(visible: boolean) {
        if (!visible || !this.wizardConfiguration?.externalAccounts) {
          return;
        }
        await this.refreshExternalAccounts();
      },
    },
  },
});
